import { Button, Grid, Group, NumberInput, Select, Text } from '@mantine/core';
import flatPriceCurrencies from '../../constants/flatPriceCurrencies';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';

const isAmountSet = (amount) => amount !== null && amount !== undefined && amount !== '';

// The API takes the pair or neither, with a positive amount, and answers
// anything else with a 422 — so the form has to refuse a half-filled pair
// rather than let the save fail. Null when the pair is savable, otherwise the
// message keyed by the input at fault. Exported for the page's save button.
export const flatPriceError = (account) => {
  const amountSet = isAmountSet(account.flatPriceAmount);
  const currencySet = Boolean(account.flatPriceCurrency);
  if (!amountSet) {
    return currencySet ? { amount: 'Enter an amount, or clear the currency.' } : null;
  }
  if (!(parseFloat(account.flatPriceAmount) > 0)) {
    return { amount: 'The flat price must be greater than 0.' };
  }
  return currencySet ? null : { currency: 'Pick a currency.' };
};

// The account carries its domain list (the same one AccountSubscriptions renders
// on the neighbouring tab), so the Shopify rule covers every shop on the account
// rather than only the one being viewed. `account.domains` isn't guaranteed to
// be present on the redux account (it's optional-chained at
// AccountSubscriptions.jsx:107, and a PUT response can replace the whole
// account without it), so the page's own, always-populated `isShopify` is
// OR-ed in as a fail-safe floor.
const accountHasShopifyDomain = (account) => (account.domains || []).some((d) => d.platform === 'shopify');

const AccountFlatPriceSection = ({ account, setAccount, isShopify }) => {
  const error = flatPriceError(account);
  const setFlatPrice = (changes) => setAccount((prev) => ({ ...prev, ...changes }));
  const hasFlatPrice = isAmountSet(account.flatPriceAmount) && Boolean(account.flatPriceCurrency);

  if (isShopify || accountHasShopifyDomain(account)) {
    return (
      <SuperAdminArea>
        <Text fw={500}>Account-level flat price</Text>
        <Text size="sm" c="dimmed" mb={hasFlatPrice ? 'xs' : undefined}>
          Not available for accounts that contain a Shopify shop.
        </Text>
        {hasFlatPrice && (
          <>
            <Text size="sm" mb="xs">
              Currently set to{' '}
              {parseFloat(account.flatPriceAmount).toLocaleString('en-US', {
                style: 'currency',
                currency: account.flatPriceCurrency,
              })}{' '}
              per month — remove it to stop billing at the account level.
            </Text>
            <Button
              variant="default"
              size="xs"
              type="button"
              onClick={() => setFlatPrice({ flatPriceAmount: null, flatPriceCurrency: null })}
            >
              Clear
            </Button>
          </>
        )}
      </SuperAdminArea>
    );
  }

  return (
    <SuperAdminArea>
      <Text fw={500} mb="xs">
        Account-level flat price
      </Text>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="Amount"
            min={0}
            decimalScale={2}
            // The API returns '4000.00'; the blur clean-up would otherwise hand back the number 4000 and dirty an untouched form.
            trimLeadingZeroesOnBlur={false}
            value={account.flatPriceAmount ?? ''}
            error={error?.amount || null}
            onChange={(value) => setFlatPrice({ flatPriceAmount: isAmountSet(value) ? String(value) : null })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Currency"
            clearable
            data={flatPriceCurrencies}
            value={account.flatPriceCurrency ?? null}
            error={error?.currency || null}
            onChange={(value) => setFlatPrice({ flatPriceCurrency: value })}
          />
        </Grid.Col>
      </Grid>
      <Group justify="space-between" align="flex-end" mt="xs" wrap="nowrap">
        <Text size="xs" c="dimmed">
          Replaces every per-domain usage line on this account&apos;s monthly invoice with a single flat fee. Usage is
          still tracked.
        </Text>
        <Button
          variant="default"
          size="xs"
          type="button"
          disabled={!isAmountSet(account.flatPriceAmount) && !account.flatPriceCurrency}
          onClick={() => setFlatPrice({ flatPriceAmount: null, flatPriceCurrency: null })}
        >
          Clear
        </Button>
      </Group>
    </SuperAdminArea>
  );
};

export default AccountFlatPriceSection;
